import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";

const NAV_ITEMS = [
  { to: "/chat", label: "Chat", icon: ChatIcon },
  { to: "/agents", label: "Agents", icon: AgentIcon },
  { to: "/agents/catalog", label: "Catalog", icon: CatalogIcon },
  { to: "/workflows", label: "Workflows", icon: WorkflowIcon },
  { to: "/skills", label: "Skills", icon: SkillIcon },
  { to: "/tools", label: "Tools", icon: ToolsIcon },
  { to: "/channels", label: "Channels", icon: ChannelsIcon },
  { to: "/activity", label: "Activity", icon: ActivityIcon },
  { to: "/audit", label: "Audit", icon: AuditIcon },
  { to: "/admin", label: "Admin", icon: AdminIcon },
];

export function AppShell() {
  const { user, logout } = useAuth();

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-ink-100 bg-white">
        {/* Logo */}
        <div className="flex h-14 items-center px-5">
          <span className="text-lg font-bold tracking-tight text-ink-950">
            flax
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-0.5 px-3 py-2">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-ink-950 text-white"
                    : "text-ink-600 hover:bg-surface-2 hover:text-ink-900"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-ink-100 p-3">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-700">
              {user?.displayName?.charAt(0).toUpperCase() ?? "?"}
            </div>
            <div className="flex-1 truncate">
              <p className="truncate text-sm font-medium text-ink-800">
                {user?.displayName ?? "User"}
              </p>
              <p className="truncate text-xs text-ink-400">
                {user?.email ?? ""}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="mt-1 w-full rounded-lg px-3 py-1.5 text-left text-xs text-ink-500 hover:bg-surface-2 hover:text-ink-700"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

// ── Inline SVG icons (16x16) ────────────────────────────────────────────────

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5l-3 3V4a1 1 0 0 1 1-1z" />
    </svg>
  );
}

function AgentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="5" r="3" />
      <path d="M2 14c0-3 2.7-5 6-5s6 2 6 5" />
    </svg>
  );
}

function WorkflowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="1" width="5" height="4" rx="1" />
      <rect x="10" y="1" width="5" height="4" rx="1" />
      <rect x="5.5" y="11" width="5" height="4" rx="1" />
      <path d="M3.5 5v2.5a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5" />
      <path d="M8 8.5V11" />
    </svg>
  );
}

function SkillIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
      <path d="M5 5h6M5 8h4" />
    </svg>
  );
}

function ToolsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.5 1.5l4 4-1.5 1.5-4-4" />
      <path d="M9 3L3 9l4 4 6-6" />
      <path d="M1 15l3-3" />
    </svg>
  );
}

function ActivityIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 8 4 8 6 3 8 13 10 6 12 8 15 8" />
    </svg>
  );
}

function CatalogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="1" width="6" height="6" rx="1" />
      <rect x="9" y="1" width="6" height="6" rx="1" />
      <rect x="1" y="9" width="6" height="6" rx="1" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  );
}

function ChannelsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 4h14M1 8h14M1 12h14" />
      <circle cx="5" cy="4" r="1.5" fill="currentColor" />
      <circle cx="11" cy="8" r="1.5" fill="currentColor" />
      <circle cx="7" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

function AuditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1L2 4v4c0 3.5 2.6 6.6 6 7.5 3.4-.9 6-4 6-7.5V4L8 1z" />
      <path d="M6 8l1.5 1.5L10 6" />
    </svg>
  );
}

function AdminIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v3l2 1" />
      <path d="M12 2l1.5 1.5M4 2L2.5 3.5M12 14l1.5-1.5M4 14l-1.5-1.5" />
    </svg>
  );
}
